import { useMemo } from 'react';
import { useInfiniteScroll } from 'src/modules/shared/hooks/scroll/movement';
import { useAnchoredScroll } from 'src/modules/shared/hooks/scroll/use-anchored-scroll';
import type { UseTimelineScrollParams } from '../../types/timeline/scroll.types';
import { timelineScrollAnchorStrategy } from '../../utils/timeline/scroll-anchor.util';

/**
 * Configures shared scrolling for upward history and newest-activity following.
 * @param params - Conversation/contact scope, filtered content and history state.
 * @returns Timeline container binding, bottom state and newest-activity action.
 */
export const useTimelineScroll = ({
  scopeKey,
  layoutRevision,
  layoutBoundaryRef,
  items,
  isFetchingHistory,
  hasMore,
  fetchMore,
}: UseTimelineScrollParams) => {
  const positionRevision = useMemo(
    () => [items, layoutRevision],
    [items, layoutRevision]
  );
  const scroll = useAnchoredScroll({
    scopeKey,
    layoutRevision,
    layoutBoundaryRef,
    contentKey: items,
    hasContent: items.length > 0,
    isUpdating: isFetchingHistory,
    anchorStrategy: timelineScrollAnchorStrategy,
    initialPosition: 'end',
    followEnd: true,
  });
  useInfiniteScroll({
    containerRef: scroll.containerRef,
    layoutBoundaryRef,
    isFetching: isFetchingHistory,
    hasMore,
    fetchMore,
    direction: 'up',
    requireUserIntent: true,
    restorePosition: false,
    resetKey: scopeKey,
    positionRevision,
    onBeforeFetch: scroll.retainPosition,
  });
  return {
    setContainer: scroll.setContainer,
    isAtBottom: scroll.isAtEnd,
    scrollToBottom: scroll.scrollToEnd,
  };
};
